import {
  DOMAIN_ROUTER_TRANSPORTS,
  serializeDomainRouterTransportInput,
  type DomainRouterTransportRouterName,
} from '@triedge/domain-core';
import { ystnjSemanticOperationManifest } from '../contracts/ystnj-semantic-operation-manifest.generated';

export type DomainOperationKind = 'query' | 'mutation';

export type DomainOperationParseResult<TValue> =
  | { success: true; data: TValue }
  | {
      success: false;
      error: {
        message?: string;
        issues?: unknown;
      };
    };

export type DomainOperationSchema<TValue = unknown> = {
  safeParse: (value: unknown) => DomainOperationParseResult<TValue>;
};

export type DomainOperationContract<TInput = unknown, TOutput = unknown> = {
  operationName?: string;
  mutationName?: string;
  inputSchema?: DomainOperationSchema<TInput>;
  outputSchema?: DomainOperationSchema<TOutput>;
};

export type DomainOperationExecutorRequest = {
  kind: DomainOperationKind;
  operationId: string;
  input: unknown;
};

export type DomainOperationExecutor = (
  request: DomainOperationExecutorRequest
) => Promise<unknown>;

export type DomainOperationTransport<
  TQueryArgsMap extends Record<string, unknown> = Record<string, unknown>,
  TQueryResultMap extends Record<string, unknown> = Record<string, unknown>,
  TMutationArgsMap extends Record<string, unknown> = Record<string, unknown>,
  TMutationResultMap extends Record<string, unknown> = Record<string, unknown>,
> = {
  query<Name extends keyof TQueryArgsMap & string>(
    name: Name,
    args: TQueryArgsMap[Name]
  ): Promise<TQueryResultMap[Name]>;
  mutate<Name extends keyof TMutationArgsMap & string>(
    name: Name,
    args: TMutationArgsMap[Name]
  ): Promise<TMutationResultMap[Name]>;
};

export type CreateDomainOperationTransportOptions<
  TQueryContracts extends Record<string, DomainOperationContract> = Record<string, DomainOperationContract>,
  TMutationContracts extends Record<string, DomainOperationContract> = Record<string, DomainOperationContract>,
> = {
  execute: DomainOperationExecutor;
  moduleId?: string;
  queryContracts?: TQueryContracts;
  mutationContracts?: TMutationContracts;
};

export class DomainOperationTransportError extends Error {
  readonly moduleId: string | undefined;
  readonly operationKind: DomainOperationKind;
  readonly operationId: string;
  readonly reason:
    | 'input-contract'
    | 'output-contract'
    | 'graphql-error'
    | 'missing-transport'
    | 'empty-response';
  readonly details: unknown;

  constructor(input: {
    moduleId?: string;
    operationKind: DomainOperationKind;
    operationId: string;
    reason:
      | 'input-contract'
      | 'output-contract'
      | 'graphql-error'
      | 'missing-transport'
      | 'empty-response';
    message: string;
    details?: unknown;
  }) {
    super(input.message);
    this.name = 'DomainOperationTransportError';
    this.moduleId = input.moduleId;
    this.operationKind = input.operationKind;
    this.operationId = input.operationId;
    this.reason = input.reason;
    this.details = input.details;
  }
}

const readContractOperationId = (
  operationId: string,
  contract: DomainOperationContract | undefined,
  kind: DomainOperationKind
): string => {
  if (!contract) {
    return operationId;
  }
  if (kind === 'query') {
    return String(contract.operationName ?? operationId);
  }
  return String(contract.mutationName ?? operationId);
};

const formatIssues = (details: unknown): string => {
  if (details == null) {
    return '';
  }
  if (typeof details === 'string') {
    return details;
  }
  try {
    return JSON.stringify(details);
  } catch {
    return String(details);
  }
};

const parseWithContract = <TValue>(input: {
  moduleId?: string;
  operationKind: DomainOperationKind;
  operationId: string;
  direction: 'input' | 'output';
  schema: DomainOperationSchema<TValue> | undefined;
  value: unknown;
}): TValue | unknown => {
  if (!input.schema) {
    return input.value;
  }
  const result = input.schema.safeParse(input.value);
  if (result.success) {
    return result.data;
  }
  const issueText = formatIssues(result.error.issues ?? result.error.message);
  throw new DomainOperationTransportError({
    moduleId: input.moduleId,
    operationKind: input.operationKind,
    operationId: input.operationId,
    reason: input.direction === 'input' ? 'input-contract' : 'output-contract',
    message: `${input.operationKind}:${input.operationId} failed ${input.direction} contract validation${issueText ? `: ${issueText}` : '.'}`,
    details: result.error,
  });
};

export const createDomainOperationTransport = <
  TQueryArgsMap extends Record<string, unknown>,
  TQueryResultMap extends Record<string, unknown>,
  TMutationArgsMap extends Record<string, unknown>,
  TMutationResultMap extends Record<string, unknown>,
  TQueryContracts extends Record<string, DomainOperationContract> = Record<string, DomainOperationContract>,
  TMutationContracts extends Record<string, DomainOperationContract> = Record<string, DomainOperationContract>,
>({
  execute,
  moduleId,
  queryContracts,
  mutationContracts,
}: CreateDomainOperationTransportOptions<TQueryContracts, TMutationContracts>): DomainOperationTransport<
  TQueryArgsMap,
  TQueryResultMap,
  TMutationArgsMap,
  TMutationResultMap
> => ({
  async query(name, args) {
    const contract = queryContracts?.[name];
    const operationId = readContractOperationId(name, contract, 'query');
    const input = parseWithContract({
      moduleId,
      operationKind: 'query',
      operationId,
      direction: 'input',
      schema: contract?.inputSchema,
      value: args,
    });
    const output = await execute({
      kind: 'query',
      operationId,
      input,
    });
    return parseWithContract({
      moduleId,
      operationKind: 'query',
      operationId,
      direction: 'output',
      schema: contract?.outputSchema,
      value: output,
    }) as TQueryResultMap[typeof name];
  },

  async mutate(name, args) {
    const contract = mutationContracts?.[name];
    const operationId = readContractOperationId(name, contract, 'mutation');
    const input = parseWithContract({
      moduleId,
      operationKind: 'mutation',
      operationId,
      direction: 'input',
      schema: contract?.inputSchema,
      value: args,
    });
    const output = await execute({
      kind: 'mutation',
      operationId,
      input,
    });
    return parseWithContract({
      moduleId,
      operationKind: 'mutation',
      operationId,
      direction: 'output',
      schema: contract?.outputSchema,
      value: output,
    }) as TMutationResultMap[typeof name];
  },
});

export type DomainRouterGraphqlResult = {
  data?: Record<string, DomainRouterTransportEnvelopeLike | null | undefined> | null;
  errors?: Array<{ message?: string | null }> | null;
};

export type DomainRouterGraphqlExecutor = (request: {
  query: string;
  variables?: Record<string, unknown>;
}) => Promise<DomainRouterGraphqlResult>;

export type DomainRouterTransportEnvelopeLike = {
  operationId?: string | null;
  payload?: unknown;
};

export type RoutedOperationTarget = {
  routerName: DomainRouterTransportRouterName;
  transportField: string;
};

export type RoutedOperationTargetMap = Partial<Record<string, DomainRouterTransportRouterName | RoutedOperationTarget>>;

export type CreateDomainRouterGraphqlTransportExecutorOptions = {
  graphql: DomainRouterGraphqlExecutor;
  fallbackQueryRouters?: RoutedOperationTargetMap;
  fallbackMutationRouters?: RoutedOperationTargetMap;
};

const buildManifestRoutedTargets = (): {
  queries: Readonly<Record<string, RoutedOperationTarget>>;
  mutations: Readonly<Record<string, RoutedOperationTarget>>;
} => {
  const queries: Record<string, RoutedOperationTarget> = {};
  const mutations: Record<string, RoutedOperationTarget> = {};

  for (const entry of ystnjSemanticOperationManifest) {
    const routerName = String(entry.runtime?.routerName ?? '').trim() as DomainRouterTransportRouterName;
    const operationId = String(entry.runtime?.operationId ?? '').trim();
    const operationKind = String(entry.runtime?.operationKind ?? '').trim();
    const transportField = String(entry.graphql?.transport?.transportField ?? '').trim();
    if (!routerName || !operationId || !transportField) {
      continue;
    }
    const target = { routerName, transportField };
    if (operationKind === 'query') {
      queries[operationId] = target;
    }
    if (operationKind === 'mutation') {
      mutations[operationId] = target;
    }
  }

  return {
    queries: Object.freeze(queries),
    mutations: Object.freeze(mutations),
  };
};

const MANIFEST_ROUTED_TARGETS = buildManifestRoutedTargets();

const resolveFallbackTarget = (
  kind: DomainOperationKind,
  candidate: DomainRouterTransportRouterName | RoutedOperationTarget | undefined
): RoutedOperationTarget | undefined => {
  if (!candidate) {
    return undefined;
  }
  if (typeof candidate !== 'string') {
    return candidate;
  }
  const transport = DOMAIN_ROUTER_TRANSPORTS[candidate];
  if (!transport) {
    return undefined;
  }
  const transportField = kind === 'query' ? transport.queryField : transport.mutationField;
  if (!transportField) {
    return undefined;
  }
  return {
    routerName: candidate,
    transportField,
  };
};

const buildTransportDocument = (
  operationKind: DomainOperationKind,
  fieldName: string
): string => /* GraphQL */ `
  ${operationKind} TriedgeDomainRouterTransport($operationId: String!, $input: AWSJSON) {
    ${fieldName}(operationId: $operationId, input: $input) {
      operationId
      payload
    }
  }
`;

const formatGraphqlErrors = (errors: DomainRouterGraphqlResult['errors']): string =>
  (errors ?? [])
    .map((entry) => String(entry?.message ?? '').trim())
    .filter(Boolean)
    .join('; ');

const parsePayloadCandidate = (value: unknown): unknown => {
  let candidate = value;
  for (let depth = 0; depth < 3; depth += 1) {
    if (typeof candidate !== 'string') {
      return candidate;
    }
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return candidate;
    }
  }
  return candidate;
};

export const createDomainRouterGraphqlTransportExecutor = ({
  graphql,
  fallbackQueryRouters,
  fallbackMutationRouters,
}: CreateDomainRouterGraphqlTransportExecutorOptions): DomainOperationExecutor => {
  const resolveTarget = (kind: DomainOperationKind, operationId: string): RoutedOperationTarget | undefined => {
    const manifestTarget = kind === 'query'
      ? MANIFEST_ROUTED_TARGETS.queries[operationId]
      : MANIFEST_ROUTED_TARGETS.mutations[operationId];
    if (manifestTarget) {
      return manifestTarget;
    }
    const fallback = kind === 'query'
      ? fallbackQueryRouters?.[operationId]
      : fallbackMutationRouters?.[operationId];
    return resolveFallbackTarget(kind, fallback);
  };

  return async ({ kind, operationId, input }) => {
    const target = resolveTarget(kind, operationId);
    if (!target) {
      throw new DomainOperationTransportError({
        operationKind: kind,
        operationId,
        reason: 'missing-transport',
        message: `${kind}:${operationId} has no routed domain transport target.`,
      });
    }

    const result = await graphql({
      query: buildTransportDocument(kind, target.transportField),
      variables: {
        operationId,
        input: serializeDomainRouterTransportInput(input),
      },
    });
    const errorMessage = formatGraphqlErrors(result.errors);
    if (errorMessage) {
      throw new DomainOperationTransportError({
        operationKind: kind,
        operationId,
        reason: 'graphql-error',
        message: `${kind}:${operationId} failed: ${errorMessage}`,
        details: result.errors,
      });
    }
    const envelope = result.data?.[target.transportField];
    if (!envelope || typeof envelope !== 'object') {
      throw new DomainOperationTransportError({
        operationKind: kind,
        operationId,
        reason: 'empty-response',
        message: `${kind}:${operationId} returned no domain transport envelope from ${target.transportField}.`,
      });
    }
    return parsePayloadCandidate(envelope.payload);
  };
};
